import moment from 'moment';
import { IZone, IMart } from '../interfaces/types';
import { IZoneExt } from '../components/pages/PageStatistics';

/**
 * Report date calculation helpers
 * Inspired by CentralWeeklyXlsxProcessor / CentralMonthlyXlsxProcessor from xlsx-processor
 */

// ============================================================================
// Date range helpers
// ============================================================================

/** Get Monday of the week containing the given date */
export function getMondayOfWeek(date: moment.Moment): moment.Moment {
    return date.clone().startOf('isoWeek');
}

/** Get Sunday of the week containing the given date */
export function getSundayOfWeek(date: moment.Moment): moment.Moment {
    return date.clone().endOf('isoWeek').startOf('day');
}

/**
 * Find the comparable Monday for N years back.
 * Same logic as CentralWeeklyXlsxProcessor:
 * Goes back N years from the reference Monday, then finds the closest Monday.
 */
export function getComparableMonday(referenceMonday: moment.Moment, yearsBack: number): moment.Moment {
    if (yearsBack === 0) {
        return referenceMonday.clone();
    }

    const sameCalendarDate = referenceMonday.clone().subtract(yearsBack, 'years');
    const mondayOfWeek = getMondayOfWeek(sameCalendarDate);
    const nextMonday = mondayOfWeek.clone().add(7, 'days');

    const distCurrent = Math.abs(sameCalendarDate.diff(mondayOfWeek, 'days'));
    const distNext = Math.abs(sameCalendarDate.diff(nextMonday, 'days'));

    return distNext < distCurrent ? nextMonday : mondayOfWeek;
}

/** Get first day of the month containing the given date, N years back */
export function getComparableMonthStart(refDate: moment.Moment, yearsBack: number): moment.Moment {
    return refDate.clone().subtract(yearsBack, 'years').startOf('month');
}

/** Get last day of the month containing the given date, N years back */
export function getComparableMonthEnd(refDate: moment.Moment, yearsBack: number): moment.Moment {
    return refDate.clone().subtract(yearsBack, 'years').endOf('month').startOf('day');
}

/** Get first day of the year, N years back */
export function getComparableYearStart(refDate: moment.Moment, yearsBack: number): moment.Moment {
    return refDate.clone().subtract(yearsBack, 'years').startOf('year');
}

/** Get last day of the year, N years back */
export function getComparableYearEnd(refDate: moment.Moment, yearsBack: number): moment.Moment {
    return refDate.clone().subtract(yearsBack, 'years').endOf('year').startOf('day');
}

// ============================================================================
// Report data types
// ============================================================================

export type ReportType = 'daily' | 'weekly' | 'monthly' | 'yearly';

export interface IReportComparisonRow {
    year: number;
    label: string;
    dateFrom: string;
    dateTo: string;
    value: number;
}

export interface IReportBreakdownRow {
    label: string;
    date?: string;
    values: {
        year: number;
        value: number;
    }[];
}

export interface IReportSection {
    id: string;
    title: string;
    subtitle?: string;
    chartType: 'bar' | 'grouped_bar' | 'line';
    /** For simple comparison (total footfall per year) */
    rows?: IReportComparisonRow[];
    /** Year columns for breakdown tables */
    columns?: { year: number; label: string }[];
    /** For breakdown tables (by location, zone, day, week, month) */
    breakdownRows?: IReportBreakdownRow[];
}

export interface IReportData {
    type: ReportType;
    referenceDate: string;
    sections: IReportSection[];
}

// ============================================================================
// Color helpers (for charts, inspired by xlsx-processor)
// ============================================================================

const YEAR_COLORS = [
    '#A81A40', // current year - brand color
    '#E8637A', // 1 year back
    '#F4A3B2', // 2 years back
    '#7B8FA1', // 3 years back
    '#B0BEC5', // 4 years back
    '#D5DDE1', // 5 years back
];

export function getYearColor(index: number): string {
    return YEAR_COLORS[index % YEAR_COLORS.length];
}

export function getYearColors(count: number): string[] {
    return Array.from({ length: count }, (_, i) => getYearColor(i));
}

// ============================================================================
// Helper to extract attendance zone from mart
// ============================================================================

export function getAttendanceZone(mart: IMart): IZone | undefined {
    return mart.zone.find(z => z.isAttendance);
}

export function getAttendanceZoneExt(zones: IZoneExt[]): IZoneExt | undefined {
    return zones.find(z => z.isAttendance);
}
